/**
 * Renders the shareable streak card to a PNG blob on a canvas — same palette
 * as the app (ink #0a0a0a, gold #c89f45, Playfair headline, JetBrains labels).
 * Portrait 1080x1350 so it drops straight into a phone story or feed post.
 *
 * The art escalates with the milestone: a plain badge under 12 weeks, a ringed
 * seal at a quarter, a double-framed foil card at half a year, and a laurelled,
 * wax-sealed "year in the chair" at 52 — so a year never looks like a month.
 */
const W = 1080;
const H = 1350;
const GOLD = "#c89f45";
const INK = "#0a0a0a";

const SERIF = '"Playfair Display", Georgia, serif';
const MONO = '"JetBrains Mono", monospace';

const TIERS = [
  { min: 52, eyebrow: "A YEAR IN THE CHAIR", ring: true, ticks: 52, doubleFrame: true, foil: true, laurel: true, glow: 0.32, seal: "SEALED · YEAR ONE" },
  { min: 26, eyebrow: "HALF A YEAR UNBROKEN", ring: true, ticks: 26, doubleFrame: true, foil: true, glow: 0.26, seal: "SEALED · 26 WEEKS" },
  { min: 12, eyebrow: "A FULL QUARTER HELD", ring: true, ticks: 12, glow: 0.22 },
  { min: 0, eyebrow: "STREAK BADGE", glow: 0.2 },
];

const tierFor = (weeks) => TIERS.find((t) => weeks >= t.min) || TIERS[TIERS.length - 1];

function tracked(ctx, text, cx, y, spacing) {
  const chars = [...text];
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);
  let x = cx - total / 2;
  chars.forEach((c, i) => {
    ctx.fillText(c, x, y);
    x += widths[i] + spacing;
  });
}

function stat(ctx, label, value, cx, y) {
  ctx.fillStyle = "#8a8a8a";
  ctx.font = `500 22px ${MONO}`;
  tracked(ctx, label.toUpperCase(), cx, y, 4);
  ctx.fillStyle = "#fafafa";
  ctx.font = `500 58px ${SERIF}`;
  ctx.textAlign = "center";
  ctx.fillText(value, cx, y + 72);
}

function ring(ctx, cx, cy, r, ticks) {
  ctx.strokeStyle = "rgba(200,159,69,0.55)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();

  if (!ticks) return;
  ctx.strokeStyle = "rgba(200,159,69,0.45)";
  ctx.lineWidth = 3;
  for (let i = 0; i < ticks; i += 1) {
    const a = (i / ticks) * Math.PI * 2 - Math.PI / 2;
    const inner = r + 16;
    const outer = r + (i % (ticks >= 26 ? 13 : 3) === 0 ? 34 : 24);
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
    ctx.lineTo(cx + Math.cos(a) * outer, cy + Math.sin(a) * outer);
    ctx.stroke();
  }
}

function laurel(ctx, cx, cy, r) {
  ctx.fillStyle = "rgba(200,159,69,0.5)";
  [-1, 1].forEach((side) => {
    for (let i = 0; i < 8; i += 1) {
      const a = (Math.PI / 2) + side * (0.72 + i * 0.105);
      const lx = cx + Math.cos(a) * (r + 54);
      const ly = cy + Math.sin(a) * (r + 54);
      ctx.save();
      ctx.translate(lx, ly);
      ctx.rotate(a + side * 0.5);
      ctx.beginPath();
      ctx.ellipse(0, 0, 24 - i * 1.4, 7.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  });
}

export async function renderStreakCard({ weeks, chairName, onTimeRate, mintedLabel }) {
  try {
    await document.fonts.ready;
  } catch {
    /* fonts fall back to Georgia / monospace */
  }

  const tier = tierFor(weeks);
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, W, H);

  // Gold glow from the top edge — deepens with the tier.
  const glow = ctx.createRadialGradient(W / 2, 0, 0, W / 2, 0, H * 0.75);
  glow.addColorStop(0, `rgba(200,159,69,${tier.glow})`);
  glow.addColorStop(1, "rgba(200,159,69,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Frames.
  ctx.strokeStyle = "rgba(200,159,69,0.35)";
  ctx.lineWidth = 2;
  ctx.strokeRect(48, 48, W - 96, H - 96);
  if (tier.doubleFrame) {
    ctx.strokeStyle = "rgba(200,159,69,0.2)";
    ctx.lineWidth = 1;
    ctx.strokeRect(68, 68, W - 136, H - 136);
    // Corner ticks.
    ctx.strokeStyle = "rgba(200,159,69,0.55)";
    ctx.lineWidth = 3;
    [[48, 48, 1, 1], [W - 48, 48, -1, 1], [48, H - 48, 1, -1], [W - 48, H - 48, -1, -1]].forEach(([x, y, dx, dy]) => {
      ctx.beginPath();
      ctx.moveTo(x + dx * 40, y);
      ctx.lineTo(x, y);
      ctx.lineTo(x, y + dy * 40);
      ctx.stroke();
    });
  }

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = GOLD;
  ctx.font = `600 24px ${MONO}`;
  tracked(ctx, `BOARDROOM · ${tier.eyebrow}`, W / 2, 180, 8);

  // Numeral, optionally ringed and laurelled.
  const cy = 480;
  const r = 232;
  if (tier.laurel) laurel(ctx, W / 2, cy, r);
  if (tier.ring) ring(ctx, W / 2, cy, r, tier.ticks);

  const size = weeks >= 100 ? 240 : tier.ring ? 300 : 380;
  ctx.font = `600 ${size}px ${SERIF}`;
  if (tier.foil) {
    const foil = ctx.createLinearGradient(0, cy - size / 2, 0, cy + size / 2);
    foil.addColorStop(0, "#f3dc9c");
    foil.addColorStop(0.5, GOLD);
    foil.addColorStop(1, "#8e6a24");
    ctx.fillStyle = foil;
  } else {
    ctx.fillStyle = GOLD;
  }
  ctx.textBaseline = "middle";
  ctx.fillText(String(weeks), W / 2, cy);
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = "#fafafa";
  ctx.font = `500 30px ${MONO}`;
  tracked(ctx, weeks === 1 ? "WEEK RUNNING" : "WEEKS RUNNING", W / 2, 790, 10);

  // Chair line, wrapped.
  const wk = weeks === 1 ? "week" : "weeks";
  const line = chairName
    ? `${chairName} has chaired their own board ${weeks} ${wk} running.`
    : `${weeks} ${wk} of chairing their own board — on the record.`;
  ctx.fillStyle = "#bbbbbb";
  ctx.font = `400 34px ${SERIF}`;
  const lines = [];
  let cur = "";
  line.split(" ").forEach((w) => {
    const next = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(next).width > W - 260 && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = next;
    }
  });
  if (cur) lines.push(cur);
  const shown = lines.slice(0, 2);
  shown.forEach((l, i) => ctx.fillText(l, W / 2, 860 + i * 48));

  // Divider + stats.
  const divY = 860 + shown.length * 48 + 40;
  const grad = ctx.createLinearGradient(180, 0, W - 180, 0);
  grad.addColorStop(0, "rgba(200,159,69,0)");
  grad.addColorStop(0.5, "rgba(200,159,69,0.55)");
  grad.addColorStop(1, "rgba(200,159,69,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(180, divY, W - 360, 2);

  const cells = [["Streak", `${weeks}w`]];
  if (onTimeRate != null) cells.push(["On time", `${onTimeRate}%`]);
  if (mintedLabel) cells.push(["Minted", mintedLabel]);
  const step = (W - 300) / cells.length;
  cells.forEach(([label, value], i) => {
    stat(ctx, label, value, 150 + step * (i + 0.5), divY + 70);
  });

  // Wax-seal pill for the big milestones.
  if (tier.seal) {
    ctx.font = `600 22px ${MONO}`;
    const textW = ctx.measureText(tier.seal).width + tier.seal.length * 6;
    const pw = textW + 60;
    const px = (W - pw) / 2;
    const py = H - 220;
    ctx.strokeStyle = "rgba(200,159,69,0.6)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(px, py, pw, 62, 31);
    ctx.stroke();
    ctx.fillStyle = "rgba(200,159,69,0.08)";
    ctx.fill();
    ctx.fillStyle = GOLD;
    tracked(ctx, tier.seal, W / 2, py + 40, 6);
  }

  ctx.fillStyle = "#7a7a7a";
  ctx.font = `500 24px ${MONO}`;
  tracked(ctx, "FOUNDTHEROOM.COM", W / 2, H - 110, 8);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

/**
 * Native share sheet when the device supports sharing files (phones), plain
 * download everywhere else.
 */
export async function shareStreakCard(blob, weeks) {
  const filename = `boardroom-${weeks}-week-streak.png`;
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: `${weeks} weeks running` });
      return "shared";
    } catch (e) {
      if (e?.name === "AbortError") return "cancelled";
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return "downloaded";
}
